import { motion } from "framer-motion";
import { CATEGORIES, LOCATOR_INSTRUCTIONS } from "../data/locations";

const LocationRow = ({ loc, i }) => (
  <motion.div
    data-testid={`locator-card-${loc.code.toLowerCase()}`}
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: i * 0.06 }}
    className="group relative border-t-2 border-[#a52a2a]/25 bg-[#ffffff] p-6 transition-all duration-300 hover:-translate-y-1 hover:border-[#a52a2a] hover:shadow-[0_18px_40px_rgba(165,42,42,0.10)] md:p-7"
  >
    <div className="grid grid-cols-[auto_1fr] gap-5 md:gap-7">
      <div className="flex flex-col items-start">
        <span
          data-testid={`locator-code-${loc.code.toLowerCase()}`}
          className="font-mono text-2xl font-bold leading-none text-[#a52a2a] md:text-3xl"
        >
          ({loc.code})
        </span>
        <span className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#f7941d]">
          {loc.place}
        </span>
      </div>

      <div>
        <h4 className="font-body text-base font-bold leading-snug text-[#2c1e1e] md:text-lg">
          {loc.name}
        </h4>
        <p className="mt-1.5 font-body text-sm leading-relaxed text-[#5a4a4a]">
          {loc.address}
        </p>
        <div className="mt-4 flex items-center gap-2 border-t border-dashed border-[#e8e2d9] pt-3">
          <span className="font-body text-[10px] font-semibold uppercase tracking-[0.18em] text-[#5a4a4a]">
            FSSAI Lic. No.
          </span>
          <span className="font-mono text-sm font-bold tracking-wide text-[#a52a2a]">
            {loc.fssai}
          </span>
        </div>
      </div>
    </div>
  </motion.div>
);

const CategoryBlock = ({ cat }) => (
  <div className="mb-14" data-testid={`locator-category-${cat.name.replace(/\s+/g, "-").toLowerCase()}`}>
    {/* Solid colored category bar */}
    <motion.div
      initial={{ opacity: 0, scaleX: 0.9 }}
      whileInView={{ opacity: 1, scaleX: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      style={{ transformOrigin: "left" }}
      className="mb-6 flex items-center justify-between rounded-sm bg-[#a52a2a] px-6 py-4"
    >
      <h3 className="font-display text-2xl font-semibold tracking-tight text-[#fdfbf7] md:text-3xl">
        {cat.name}
      </h3>
      <span className="hidden font-mono text-[10px] uppercase tracking-[0.24em] text-[#f7941d] sm:block">
        {cat.tag}
      </span>
    </motion.div>

    <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
      {cat.locations.map((loc, i) => (
        <LocationRow key={loc.code} loc={loc} i={i} />
      ))}
    </div>
  </div>
);

export const FactoryLocator = () => {
  return (
    <section
      id="locator"
      data-testid="locator"
      className="relative bg-[#fdfbf7] py-24 md:py-32"
    >
      <div className="mx-auto max-w-[1200px] px-6 md:px-10">
        {/* Brand logo */}
        <motion.img
          src="/ktv-logo.png"
          alt="KTV Health Food Private Limited"
          data-testid="brand-logo"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mb-12 h-14 w-auto md:h-16"
        />

        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mb-4 flex items-center gap-3"
        >
          <img src="/ktv-mark.png" alt="" aria-hidden className="h-9 w-9" />
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-[#a52a2a]">
            Traceability index
          </span>
        </motion.div>
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.85, ease: [0.16, 1, 0.3, 1], delay: 0.05 }}
          className="font-display text-6xl font-semibold leading-[0.9] tracking-tight text-[#2c1e1e] md:text-8xl"
        >
          Factory <span className="italic text-[#a52a2a]">locator</span>
        </motion.h2>

        {/* Instructions */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.1 }}
          data-testid="locator-instructions"
          className="mt-10 rounded-sm border border-[#f7941d]/40 bg-[#fdf6ea] p-7 md:p-9"
        >
          <h3 className="mb-5 font-display text-2xl font-semibold text-[#a52a2a] md:text-3xl">
            How to use this index
          </h3>
          <ol className="flex flex-col gap-4">
            {LOCATOR_INSTRUCTIONS.map((t, i) => (
              <li key={i} className="flex gap-4">
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#a52a2a] font-mono text-xs font-bold text-[#fdfbf7]">
                  {i + 1}
                </span>
                <p className="font-body text-sm leading-relaxed text-[#2c1e1e] md:text-base">
                  {t}
                </p>
              </li>
            ))}
          </ol>
        </motion.div>

        {/* Categories */}
        <div className="mt-16">
          {CATEGORIES.map((cat) => (
            <CategoryBlock key={cat.name} cat={cat} />
          ))}
        </div>
      </div>
    </section>
  );
};
